import * as React from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import '../Main.scss';

import {
    Col,
    Container,
    Row
} from 'reactstrap';

import {
    GetBasicSettings,
    GetTenantName,
    IsLenderDisabled,
    TenantBasicSettings,
    TenantLoginText,
    TenantActionCreators,
} from '../../Store/Tenant';
import {
    GetPortalLoginText,
    RetrieveLoginText,
    GetPortalLanguage,
} from '../../Store/PortalAssets';
import { GetRedirectAfterLogin, UIRedirectInfo } from '../../Store/UIState';
import { ApplicationState } from '../../Store';

import { ExtractUrlParameters } from '../../Utils/PathUtils';
import { pathConstants } from '../../Utils/Constants';

import { PortalTextLogin } from '../../Models/Api/strongbox.financialportal';

import { AppTitleHeader } from '../Utils/AppTitleHeader';

import { GenerateCommonLoginContent } from './CommonContent';

type InjectedReduxState = {
    tenantName: string;
    redirect?: UIRedirectInfo;
    tenantBasicSettings?: TenantBasicSettings;
    isTenantDisabled: boolean;
    portalLoginText?: PortalTextLogin;
    portalLanguage?: string;
};

type InjectedActionCreators = typeof TenantActionCreators;

type MultiTenantLoginProps = {
    defaultWelcomeText: TenantLoginText[];
};

type Props = MultiTenantLoginProps & InjectedReduxState & InjectedActionCreators;

const MultiTenantLoginComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        defaultWelcomeText,
        portalLoginText,
        portalLanguage,
    } = props;

    const location = useLocation();

    const [inactivityTimeout, setInactivityTimeout] = React.useState<boolean>(false);

    React.useEffect(() => {
        let inactivity = false;

        RetrieveLoginText();
    
        ExtractUrlParameters(
            location.search,
            (key: string, value: string): void => {
                switch (key.toLowerCase()) {
                    case pathConstants.queryParamInactivityTimeout:
                        if (value.toLowerCase() === 'true') {
                            inactivity = true;
                        }
                        break;
                }
            }
        );

        setInactivityTimeout(inactivity);
        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rowContent = React.useMemo(() => {
        return GenerateCommonLoginContent(
            defaultWelcomeText,
            '',
            true,
            1,
            false,
            portalLoginText,
            props.redirect,
            portalLanguage
        )

        // These are the actual dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portalLoginText]);

    if (props.isTenantDisabled) {
        return <Navigate to={pathConstants.tenantLenderDisabled} />
    }
    if (inactivityTimeout) {
        return <Navigate to={pathConstants.slidingWindowTimeout} />
    }

    return (
        <div
            className={`full-screen-region lender-region content-region control-region control-region-lender login-region`}
        >
                <Container className={'login-content-container login-content-elements strongbox-basic-content-container'}>
                    <AppTitleHeader />
                    <Row>
                        <Col>
                            {rowContent.map((pg, pgIndex) => (
                                <Row 
                                    className={pg.rowClassName}
                                    key={`tenant-welcome-paragraph-row-${pgIndex}`}
                                >
                                    <Col className={'col-centered'}>
                                        {pg.content}
                                    </Col>
                                </Row>
                            ))}
                        </Col>
                    </Row>
                </Container>
        </div>
    );
}

export const MultiTenantLogin = connect<InjectedReduxState, InjectedActionCreators, MultiTenantLoginProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            tenantName: GetTenantName(appState),
            redirect: GetRedirectAfterLogin(appState),
            tenantBasicSettings: GetBasicSettings(appState),
            isTenantDisabled: IsLenderDisabled(appState),
            portalLoginText: GetPortalLoginText(appState),
            portalLanguage: GetPortalLanguage(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...TenantActionCreators,
        },
        dispatch
    )
)(MultiTenantLoginComponent);
